import {LoadingOutlined} from "@ant-design/icons";
import Collapse from "@kunukn/react-collapse";
import {Breadcrumb, Button, Checkbox, Descriptions, Form, Input, InputNumber, message, Modal, Select, Skeleton, Spin, Switch, Table, Tag} from "antd";
import {useForm} from "antd/es/form/Form";
import React, {useContext, useEffect, useState} from "react";
import {Link, useNavigate, useParams} from "react-router-dom";
import {AiExtractorServiceContext, AppContextContext, OrganizationServiceContext, TagServiceContext, VulSourceServiceContext, VulViewServiceContext} from "../../Contexts";
import Organization from "../../domain/Organization";
import {ResourceOwnerType} from "../../domain/ResourceOwnerType";
import VulSource from "../../domain/VulSource";
import AiExtractor, {isActivelyManagedByInterests} from "../../domain/AiExtractor";
import {formatVulSourceType, VulSourceType} from "../../domain/VulSourceType";
import QueryOptions from "../../sal-ui/QueryOptions";
import {ServerConstraintViolationsHolder} from "../../sal-ui/ServerConstraintViolations";
import {formatDateTime, syntaxHighlight} from "../../utils/FormatUtils";
import * as globalStyles from "../App.module.css";
import {DocumentTitle} from "../DocumentTitle";
import {formatRssGuidSource, RssGuidSource} from "../../domain/RssGuidSource";
import * as styles from "./VulSourceDetail.module.css";
import {useTableHandler} from "../../sal-ui/TableHandler";
import PagedResult from "../../service/PagedResult";
import VulView from "../../domain/VulView";
import {ColumnsType} from "antd/es/table";
import ReadOnlyQueryView from "../vulview/ReadOnlyQueryView";
import TextArea from "antd/lib/input/TextArea";
import MarkdownContent from "../common/MarkdownContent";
import {Permission} from "../../domain/auth/Permission";
import {buildAiExtractorSelectOptions} from "../../utils/AiExtractorSelectUtils";
import {formatVulSourceHealth, VulSourceHealth} from "../../domain/VulSourceHealth";
import {renderOrganizationLink} from "../common/OrganizationLink";
import {FormPersister} from "../../sal-ui/FormPersister";
import SelectAllMultiSelect from "../common/SelectAllMultiSelect";
import {ascendingFirstSortDirections} from "../../sal-ui/TableSortDirections";

const serverViolationsHolder = new ServerConstraintViolationsHolder();

function VulSourceDetail() {
    const persistentIdent = 'VulSourceDetailViews';
    const appContext = useContext(AppContextContext);
    const vulSourceService = useContext(VulSourceServiceContext);
    const aiExtractorService = useContext(AiExtractorServiceContext);
    const organizationService = useContext(OrganizationServiceContext);
    const vulViewService = useContext(VulViewServiceContext);
    const tagService = useContext(TagServiceContext);
    const navigate = useNavigate();
    const {vulSourceId} = useParams<{ vulSourceId: string }>();
    const [editMode, setEditMode] = useState(false);
    const [source, setSource] = useState<VulSource>();
    const [editForm] = useForm();
    const [owner, setOwner] = useState<ResourceOwnerType>(ResourceOwnerType.SYSTEM);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [views, setViews] = useState<PagedResult<VulView>>();
    const [sources, setSources] = useState<VulSource[]>();
    const [tags, setTags] = useState<string[]>();
    const [allAiExtractors, setAllAiExtractors] = useState<AiExtractor[]>();
    const [selectedOrgAiExtractorIds, setSelectedOrgAiExtractorIds] = useState<string[]>([]);
    const [orgAiExtractorEditMode, setOrgAiExtractorEditMode] = useState(false);
    const [orgAiExtractorSaving, setOrgAiExtractorSaving] = useState(false);
    const [selectedSystemAiExtractorIds, setSelectedSystemAiExtractorIds] = useState<string[]>([]);
    const [systemAiExtractorEditMode, setSystemAiExtractorEditMode] = useState(false);
    const [systemAiExtractorSaving, setSystemAiExtractorSaving] = useState(false);
    const aiExtractorById = new Map((allAiExtractors ?? []).map(aiExtractor => [aiExtractor.id, aiExtractor]));
    const systemAssignedAiExtractors = getAssignedAiExtractors(source?.aiExtractorAssignments, ResourceOwnerType.SYSTEM);
    const organizationAssignedAiExtractors = getAssignedAiExtractors(source?.aiExtractorAssignments, ResourceOwnerType.ORGANIZATION);
    const editableAiExtractors = (allAiExtractors ?? []).filter(aiExtractor => !isActivelyManagedByInterests(aiExtractor));
    const editableAiExtractorIds = new Set(editableAiExtractors.flatMap(aiExtractor => aiExtractor.id ? [aiExtractor.id] : []));
    const editableSystemAssignedAiExtractorIds = getEditableAssignedAiExtractorIds(systemAssignedAiExtractors);
    const editableOrganizationAssignedAiExtractorIds = getEditableAssignedAiExtractorIds(organizationAssignedAiExtractors);

    const tableHandler = useTableHandler("name asc", {
        reloadFunction: reloadViews,
        initialPageSize: 25,
        persistentIdent
    });

    const layout = {
        labelCol: {span: 8},
        wrapperCol: {span: 20},
    };

    const tailLayout = {
        wrapperCol: {offset: 8, span: 20},
    };

    const columns: ColumnsType<VulView> = [
        {
            dataIndex: "name",
            title: "Name",
            sorter: true,
            sortDirections: ascendingFirstSortDirections,
            defaultSortOrder: "ascend",
            render: (value, vulView) => <>
                <Link to={`/vul-source-items/views/${vulView.id}`}>{value}</Link>

                <MarkdownContent className={styles['view-description']} children={vulView.description}/>
            </>
        },
        {
            dataIndex: "query",
            title: "Query",
            width: 600,
            render: value => <ReadOnlyQueryView sources={sources} query={JSON.parse(value)}/>
        },
        {
            dataIndex: ["organization", "name"],
            title: "Organization",
            sorter: true,
            sortDirections: ascendingFirstSortDirections,
            align: "left",
            render: renderOrganization
        },
    ];

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        vulSourceService.getList(QueryOptions.newUnlimitedOrderedInstance("name")).then(value => setSources(value.data));

        if (appContext.user.isSystemAdmin()) {
            organizationService.getList(new QueryOptions("name", 100, 0)).then(value => {
                setOrganizations(value.data);
            });

            tagService.getList().then(setTags);
        }

        if (appContext.user.isSystemAdmin() || appContext.user.hasPermission(Permission.AI_EXTRACTOR__MANAGE_ORG)) {
            aiExtractorService.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
                .then(result => setAllAiExtractors(result.data));
        }

        reload();

        if (appContext.user.hasOneOfPermission([Permission.VIEW__READ_ORG, Permission.VIEW__READ_SEAL])) {
            reloadViews();
        }
    }, [vulSourceId])

    if (!source) return <Skeleton/>;

    return (
        <DocumentTitle title={source.name}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}>
                    <Breadcrumb.Item>{appContext.config?.appName}</Breadcrumb.Item>
                    <Breadcrumb.Item><Link to={"/vul-sources"}>Vulnerability sources</Link></Breadcrumb.Item>
                    <Breadcrumb.Item>{source.name}</Breadcrumb.Item>
                </Breadcrumb>

                <Spin spinning={!source} indicator={<LoadingOutlined style={{fontSize: 24}} spin={true}/>}>
                    <h1>{source.name}</h1>

                    {(appContext.user.isSystemAdmin() || (source.owner === ResourceOwnerType.ORGANIZATION && appContext.user.hasPermission(Permission.SOURCE__MANAGE_ORG))) &&
                        <div className={globalStyles["common__top-button-bar"]}>
                            <Button onClick={() => setEditMode(!editMode)}>Edit</Button>

                            <Button danger={true}
                                    title={"Delete"}
                                    onClick={() => {
                                        Modal.confirm({
                                            content: "Do you really want to delete this source?",
                                            okText: "Delete",
                                            cancelText: "Cancel",
                                            okButtonProps: {danger: true},
                                            onOk: () => onDeleteConfirm(source!)
                                        });
                                    }}>Delete
                            </Button>

                            {(appContext.user.isSystemAdmin() || source.owner === ResourceOwnerType.ORGANIZATION) &&
                                <Button
                                    onClick={() => {
                                        Modal.confirm({
                                            content: "Do you really want to collect vulnerability items for this source?",
                                            okText: "Collect",
                                            cancelText: "Cancel",
                                            onOk: () => onRunConfirm(source)
                                        });
                                    }}>
                                    Collect
                                </Button>
                            }

                            {appContext.user.isSystemAdmin() &&
                                <>
                                    <Button
                                        onClick={() => {
                                            Modal.confirm({
                                                content: "Do you really want to update vulnerability items in OpenSearch?",
                                                okText: "Update",
                                                cancelText: "Cancel",
                                                onOk: () => onUpdateInOpenSearchConfirm(source)
                                            });
                                        }}>
                                        Update in OpenSearch
                                    </Button>

                                    <Button
                                        danger={true}
                                        onClick={() => {
                                            Modal.confirm({
                                                content: "Do you really want to purge items for this source? All items will be deleted (without audit log).",
                                                okText: "Purge",
                                                cancelText: "Cancel",
                                                onOk: () => onPurgeConfirm(source),
                                            });
                                        }}>
                                        Purge
                                    </Button>
                                </>
                            }
                        </div>
                    }

                    {renderEditForm()}

                    <div className={styles['details']}>
                        <Descriptions column={1} bordered={true} size="small" className={globalStyles.details} title={"Source properties"}>
                            {source.owner === ResourceOwnerType.ORGANIZATION && <Descriptions.Item label={"Owner"}>{source.organization?.name}</Descriptions.Item>}
                            {source.owner === ResourceOwnerType.SYSTEM && <Descriptions.Item label={"Owner"}><i>System source</i></Descriptions.Item>}

                            <Descriptions.Item label={"Description"}>
                                {source?.description && <MarkdownContent children={source?.description}/>}
                                {!source?.description && <i>no description</i>}
                            </Descriptions.Item>

                            <Descriptions.Item label={"Type"}>{formatVulSourceType(source.sourceType)}</Descriptions.Item>

                            {source.sourceType === VulSourceType.NVD20 &&
                                <Descriptions.Item label={"API key"}>{source.apiKey}</Descriptions.Item>
                            }

                            {source.sourceType === VulSourceType.RSS &&
                                <>
                                    <Descriptions.Item label={"URL"}>{source.url}</Descriptions.Item>
                                    <Descriptions.Item label={"GUID source"}>{formatRssGuidSource(source.guidSource)}</Descriptions.Item>
                                    <Descriptions.Item label={"User agent"}>{source.userAgent || <i>default</i>}</Descriptions.Item>
                                </>
                            }

                            {source.sourceType === VulSourceType.TWITTER &&
                                <Descriptions.Item label={"Username"}>{source.username}</Descriptions.Item>
                            }

                            {source.sourceType === VulSourceType.OSV && <>
                                <Descriptions.Item label={"Ecosystem whitelist"}>
                                    {(source.ecosystemWhitelist && source.ecosystemWhitelist.length > 0)
                                        ? source.ecosystemWhitelist.map(value => <Tag key={value}>{value}</Tag>)
                                        : <i>all</i>}
                                </Descriptions.Item>
                                <Descriptions.Item label={"Ecosystem blacklist"}>
                                    {(source.ecosystemBlacklist && source.ecosystemBlacklist.length > 0)
                                        ? source.ecosystemBlacklist.map(value => <Tag key={value}>{value}</Tag>)
                                        : <i>none</i>}
                                </Descriptions.Item>
                            </>}

                            <Descriptions.Item label={"Include affected products in AI input"}><Checkbox checked={source.includeAffectedProductsInAi} disabled={true}/></Descriptions.Item>
                            <Descriptions.Item label={"Enabled"}><Checkbox checked={source.enabled} disabled={true}/></Descriptions.Item>
                            <Descriptions.Item label={"Last collection"}>{formatDateTime(source.lastRun)}</Descriptions.Item>
                            <Descriptions.Item label={"Latest item published at"}>{formatDateTime(source.latestItemPublishedAt)}</Descriptions.Item>
                            <Descriptions.Item label={"Health status"}>
                                {source.health === VulSourceHealth.HEALTHY && <Tag color="green">{formatVulSourceHealth(source.health)}</Tag>}
                                {source.health === VulSourceHealth.LAST_RUN_OUT_OF_DATE && <Tag color="red">{formatVulSourceHealth(source.health)}</Tag>}
                                {source.health === VulSourceHealth.LATEST_ITEM_PUBLISHED_AT_OUT_OF_DATE && <Tag color="orange">{formatVulSourceHealth(source.health)}</Tag>}
                            </Descriptions.Item>
                            <Descriptions.Item label={"Items"}>{renderItemCount()}</Descriptions.Item>
                        </Descriptions>

                        {(appContext.user.isSystemAdmin() || appContext.user.hasPermission(Permission.AI_EXTRACTOR__MANAGE_ORG)) &&
                            <div className={styles['details-column']}>
                                {appContext.user.isSystemAdmin() &&
                                    <Descriptions column={1} bordered={true} size="small" className={globalStyles.details} title={"System (hidden) properties"}>
                                        <Descriptions.Item label={"Tags"}>{source.tags?.map(value => <Tag key={value}>{value}</Tag>)}</Descriptions.Item>
                                        <Descriptions.Item label={"Imported at"}>{formatDateTime(source.importedAt)}</Descriptions.Item>
                                        <Descriptions.Item label={"Initial collection done"}><Checkbox checked={source.initialCollectionDone} disabled={true}/>{source.initialCollectionDoneAt && <span style={{marginLeft: 8}}>{formatDateTime(source.initialCollectionDoneAt)}</span>}</Descriptions.Item>
                                        <Descriptions.Item label={"Max. items per collection"}>{source.maxItemsPerCollection ?? <i>default</i>}</Descriptions.Item>
                                        <Descriptions.Item label={"Poll state"}>
                                            <div className={globalStyles["formatted-json"]} dangerouslySetInnerHTML={{__html: `${source?.pollState && syntaxHighlight(source.pollState)}`}}/>
                                        </Descriptions.Item>
                                    </Descriptions>
                                }

                                {shouldShowSystemAiExtractorAssignments() &&
                                    <Descriptions column={1} bordered={true} size="small" className={globalStyles.details} title={"Assignments by system"}>
                                        <Descriptions.Item label={"AI extractors"}>
                                            {!allAiExtractors ? <Spin size="small"/> : !systemAiExtractorEditMode ? (
                                                <>
                                                    {systemAssignedAiExtractors.length > 0 ? (
                                                        <div className={styles["ai-extractor-assignment-list"]}>
                                                            {systemAssignedAiExtractors.map(aiExtractor => (
                                                                renderAssignedAiExtractor(aiExtractor)
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <div><i>no AI extractors</i></div>
                                                    )}
                                                    {canEditSystemAiExtractorAssignments() &&
                                                        <Button size="small" style={{marginTop: 8}} onClick={() => {
                                                            setSelectedSystemAiExtractorIds(editableSystemAssignedAiExtractorIds);

                                                            setSystemAiExtractorEditMode(true);
                                                        }}>Edit</Button>
                                                    }
                                                </>
                                            ) : (
                                                <>
                                                    {renderAiExtractorAssignmentEditor(
                                                        selectedSystemAiExtractorIds,
                                                        setSelectedSystemAiExtractorIds,
                                                        buildAiExtractorSelectOptions(
                                                            editableAiExtractors,
                                                            appContext.user.isSystemAdmin(),
                                                            'SYSTEM'
                                                        )
                                                    )}
                                                    <div style={{marginTop: 8}}>
                                                        <Button type="primary" size="small" loading={systemAiExtractorSaving} onClick={onSaveSystemAiExtractors}>Save</Button>
                                                        <Button size="small" style={{marginLeft: 8}} onClick={() => setSystemAiExtractorEditMode(false)}>Cancel</Button>
                                                    </div>
                                                </>
                                            )}
                                        </Descriptions.Item>
                                    </Descriptions>
                                }

                                {shouldShowOrganizationAiExtractorAssignments() &&
                                    <Descriptions column={1} bordered={true} size="small" className={globalStyles.details} title={"Assignments by organization"}>
                                        <Descriptions.Item label={"AI extractors"}>
                                            {!allAiExtractors ? <Spin size="small"/> : !orgAiExtractorEditMode ? (
                                                <>
                                                    {organizationAssignedAiExtractors.length > 0 ? (
                                                        <div className={styles["ai-extractor-assignment-list"]}>
                                                            {organizationAssignedAiExtractors.map(aiExtractor => (
                                                                renderAssignedAiExtractor(aiExtractor)
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <div><i>no AI extractors</i></div>
                                                    )}
                                                    {canEditOrganizationAiExtractorAssignments() &&
                                                        <Button size="small" style={{marginTop: 8}} onClick={() => {
                                                            setSelectedOrgAiExtractorIds(editableOrganizationAssignedAiExtractorIds);

                                                            setOrgAiExtractorEditMode(true);
                                                        }}>Edit</Button>
                                                    }
                                                </>
                                            ) : (
                                                <>
                                                    {renderAiExtractorAssignmentEditor(
                                                        selectedOrgAiExtractorIds,
                                                        setSelectedOrgAiExtractorIds,
                                                        buildOrganizationAssignmentAiExtractorOptions()
                                                    )}
                                                    <div style={{marginTop: 8}}>
                                                        <Button type="primary" size="small" loading={orgAiExtractorSaving} onClick={onSaveOrgAiExtractors}>Save</Button>
                                                        <Button size="small" style={{marginLeft: 8}} onClick={() => setOrgAiExtractorEditMode(false)}>Cancel</Button>
                                                    </div>
                                                </>
                                            )}
                                        </Descriptions.Item>
                                    </Descriptions>
                                }
                            </div>
                        }
                    </div>

                    {appContext.user.hasOneOfPermission([Permission.VIEW__READ_ORG, Permission.VIEW__READ_SEAL]) && <>
                        <h2>Usage in views</h2>

                        <Table className={styles.table}
                               showSorterTooltip={false}
                               dataSource={views?.data}
                               locale={{emptyText: 'This source is not used in any view.'}}
                               size="middle"
                               onChange={tableHandler.onTableChange}
                               pagination={tableHandler.pagination}
                               rowKey="id"
                               columns={columns}
                        />
                    </>}
                </Spin>
            </>
        </DocumentTitle>
    )

    function onRunConfirm(vulSource: VulSource) {
        vulSourceService.run(vulSource)
            .then(() => {
                message.success("Collection of vulnerability items started.");

                reload();
            });
    }

    function onPurgeConfirm(vulSource: VulSource) {
        vulSourceService.purge(vulSource)
            .then(() => {
                message.success("All items purged successfully.");

                reload();
            });
    }

    function onUpdateInOpenSearchConfirm(vulSource: VulSource) {
        vulSourceService.updateInOpenSearch(vulSource)
            .then(() => {
                message.success("Vulnerability items updated in OpenSearch successfully.");

                reload();
            });
    }

    function renderEditForm() {
        if (!source) return <Skeleton/>;

        return (
            <Collapse isOpen={editMode}>
                <h3>Edit of vulnerability source</h3>

                <Form {...layout} form={editForm} className={`${globalStyles["common__form"]} ${globalStyles["common_form--edit"]}`} onFinish={onFinishEdit}>
                    <Form.Item
                        name={"name"}
                        label={"Name"}
                        rules={[
                            {required: true, message: "Name is required."},
                            {validator: serverViolationsHolder.createServerValidator('UNIQUE', 'name', undefined, {compareLowerCaseValues: true}), message: "name-not-unique"}
                        ]}>
                        <Input maxLength={100}/>
                    </Form.Item>

                    <Form.Item
                        name={"description"}
                        label={"Description"}
                        extra={"Description of vulnerability source in CommonMark format."}>
                        <TextArea maxLength={1000} autoSize={{minRows: 4, maxRows: 8}}/>
                    </Form.Item>

                    {source.sourceType === VulSourceType.NVD20 &&
                        <Form.Item
                            name={"apiKey"}
                            label={"API key"}
                            rules={[
                                {required: true, message: "API key is required."},
                            ]}>
                            <Input maxLength={100}/>
                        </Form.Item>
                    }

                    {source.sourceType === VulSourceType.RSS &&
                        <>
                            <Form.Item
                                name={"url"}
                                label={"URL"}
                                extra={"HTTP or HTTPS address of Atom / RSS feed."}
                                rules={[
                                    {required: true, message: "URL is required."},
                                ]}>
                                <Input maxLength={1000}/>
                            </Form.Item>

                            <Form.Item
                                name={["guidSource"]}
                                label={"GUID source"}
                                extra={"RSS item attribute used for deduplication."}
                                rules={[{required: true, message: "GUID source is required."}]}>
                                <Select
                                    options={[
                                        {label: formatRssGuidSource(RssGuidSource.GUID), value: 'GUID'},
                                        {label: formatRssGuidSource(RssGuidSource.LINK), value: 'LINK'},
                                        {label: formatRssGuidSource(RssGuidSource.TITLE), value: 'TITLE'},
                                        {label: formatRssGuidSource(RssGuidSource.DESCRIPTION), value: 'DESCRIPTION'},
                                    ]}
                                />
                            </Form.Item>

                            <Form.Item
                                name={"userAgent"}
                                label={"User agent"}
                                extra={"Optional content of User-Agent HTTP header."}>
                                <Input maxLength={1000}/>
                            </Form.Item>
                        </>
                    }

                    {source.sourceType === VulSourceType.TWITTER &&
                        <Form.Item
                            name={"username"}
                            label={"Username"}
                            rules={[
                                {required: true, message: "Username is required."},
                            ]}>
                            <Input maxLength={100}/>
                        </Form.Item>
                    }

                    {source.sourceType === VulSourceType.CISCO_OPENVULN && <>
                        <Form.Item
                            name={"apiKey"}
                            label={"API key"}
                            rules={[
                                {required: true, message: "API key is required."},
                            ]}>
                            <Input maxLength={100}/>
                        </Form.Item>

                        <Form.Item
                            name={"apiClientSecret"}
                            label={"API client secret"}
                            rules={[
                                {required: true, message: "API client secret is required."},
                            ]}>
                            <Input maxLength={100}/>
                        </Form.Item>
                    </>
                    }

                    {source.sourceType === VulSourceType.OSV && <>
                        <Form.Item
                            name={"ecosystemWhitelist"}
                            label={"Ecosystem whitelist"}
                            extra={"If empty, all ecosystems are loaded (except blacklisted)."}>
                            <Select mode="tags" placeholder={"e.g. Maven, PyPI, Go"}/>
                        </Form.Item>

                        <Form.Item
                            name={"ecosystemBlacklist"}
                            label={"Ecosystem blacklist"}
                            extra={"Excluded ecosystems (applies always)."}>
                            <Select mode="tags" placeholder={"e.g. crates.io"}/>
                        </Form.Item>
                    </>}

                    {appContext.user.isSystemAdmin() && <>
                        <Form.Item
                            name={["owner"]}
                            label={"Owner"}
                            rules={[{required: true, message: "Owner is required."}]}>
                            <Select onChange={value => setOwner(value)}>
                                <Select.Option key={ResourceOwnerType.SYSTEM} value={ResourceOwnerType.SYSTEM}>system</Select.Option>
                                <Select.Option key={ResourceOwnerType.ORGANIZATION} value={ResourceOwnerType.ORGANIZATION}>organization</Select.Option>
                            </Select>
                        </Form.Item>

                        {owner === ResourceOwnerType.ORGANIZATION &&
                            <Form.Item
                                name={"organization"}
                                label={"Organization"}
                                rules={[{required: true, message: "Organization is required."}]}>
                                <Select allowClear>
                                    {organizations.map(organization => (
                                        <Select.Option key={organization.id} value={organization.id}>
                                            {organization.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        }
                    </>}

                    {appContext.user.isSystemAdmin() && <Form.Item
                        name={["tags"]}
                        label={"Tags"}>
                        <Select mode="tags" options={tags?.map(tag => ({value: tag, label: tag}))}/>
                    </Form.Item>}

                    <Form.Item
                        name={"includeAffectedProductsInAi"}
                        label={"Include affected products in AI input"}
                        extra={"Include affected product names in AI extraction and summaries."}
                        initialValue={false}
                        valuePropName={"checked"}>
                        <Switch/>
                    </Form.Item>

                    <Form.Item
                        name={["enabled"]}
                        label={"Enabled"}
                        extra={"Source is polled periodically if enabled."}
                        valuePropName={"checked"}>
                        <Switch/>
                    </Form.Item>

                    {appContext.user.isSystemAdmin() && <>
                        <Form.Item
                            name={["initialCollectionDone"]}
                            label={"Initial collection done"}
                            valuePropName={"checked"}>
                            <Switch/>
                        </Form.Item>
                    </>}

                    <Form.Item
                        name={"maxLastRunAge"}
                        label={"Max. last collection age"}
                        extra={"Duration in hours since the last collection that is still considered healthy."}
                        initialValue={24}
                        rules={[
                            {required: true, message: "Max. last collection age is required."},
                        ]}>
                        <InputNumber min={6} max={87600}/>
                    </Form.Item>

                    <Form.Item
                        name={"maxLatestItemPublishedAtAge"}
                        label={"Max. latest published item age"}
                        extra={"Duration in hours since the latest published item that is still considered healthy."}
                        initialValue={168}
                        rules={[
                            {required: true, message: "Max. latest published item age is required."},
                        ]}>
                        <InputNumber min={6} max={87600}/>
                    </Form.Item>

                    {appContext.user.isSystemAdmin() &&
                        <Form.Item
                            name={"maxItemsPerCollection"}
                            label={"Max. items per collection"}
                            extra={"Optional per-source override of max items downloaded during one collection run."}>
                            <InputNumber min={1} max={1000000}/>
                        </Form.Item>
                    }

                    <Form.Item {...tailLayout} className={globalStyles["common__form-buttons"]}>
                        <Button type={"primary"} htmlType={"submit"}>{"Save"}</Button>
                        <Button onClick={() => setEditMode(false)}>{"Cancel"}</Button>
                    </Form.Item>
                </Form>
            </Collapse>
        )
    }

    function renderItemCount() {
        const filter = {
            typeHints: {},
            values: {
                sources: [source.id]
            }
        };

        const searchParams = new URLSearchParams();

        searchParams.set("filter", FormPersister.encodeUrlState(filter))
        searchParams.set("$readOnly", "");

        return <a href={`/vul-source-items?${searchParams.toString()}`} target={"_blank"}>{source.itemCount}</a>;
    }

    function renderOrganization(organizationName: string | undefined, vulView: VulView) {
        if (vulView.owner === ResourceOwnerType.SYSTEM) {
            return <i>System view</i>;
        }

        return renderOrganizationLink(vulView.organization, organizationName);
    }

    function renderAssignedAiExtractor(aiExtractor: AiExtractor) {
        return (
            <div key={aiExtractor.id} className={styles["ai-extractor-assignment"]}>
                <Tag
                    variant={"filled"}
                    color={assignedAiExtractorOwnerColor(aiExtractor)}
                    className={styles["ai-extractor-assignment-tag"]}
                    title={aiExtractor.owner === ResourceOwnerType.SYSTEM ? "System AI extractor" : "Organization AI extractor"}>
                    {formatAssignedAiExtractorOwner(aiExtractor)}
                </Tag>
                <Link to={`/definitions/ai-extractors/${aiExtractor.id}`}>{aiExtractor.name || aiExtractor.id}</Link>
                {renderAssignedAiExtractorOrganization(aiExtractor)}
            </div>
        );
    }

    function formatAssignedAiExtractorOwner(aiExtractor: AiExtractor) {
        return aiExtractor.owner === ResourceOwnerType.SYSTEM ? "sys" : "org";
    }

    function assignedAiExtractorOwnerColor(aiExtractor: AiExtractor) {
        return aiExtractor.owner === ResourceOwnerType.SYSTEM ? "#440084" : "#0066cc";
    }

    function renderAssignedAiExtractorOrganization(aiExtractor: AiExtractor) {
        if (!appContext.user.isSystemAdmin()
            || aiExtractor.owner !== ResourceOwnerType.ORGANIZATION
            || !aiExtractor.organization?.name) {
            return null;
        }

        return <span className={styles["ai-extractor-assignment-organization"]}>{aiExtractor.organization.name}</span>;
    }

    function onSaveSystemAiExtractors() {
        setSystemAiExtractorSaving(true);

        vulSourceService.updateAiExtractors(vulSourceId!, selectedSystemAiExtractorIds, 'system')
            .then(() => {
                message.success("Assignments updated.");

                return reload();
            })
            .then(() => setSystemAiExtractorEditMode(false))
            .finally(() => setSystemAiExtractorSaving(false));
    }

    function onSaveOrgAiExtractors() {
        setOrgAiExtractorSaving(true);

        vulSourceService.updateAiExtractors(vulSourceId!, selectedOrgAiExtractorIds, 'organization')
            .then(() => {
                message.success("Assignments updated.");

                return reload();
            })
            .then(() => setOrgAiExtractorEditMode(false))
            .finally(() => setOrgAiExtractorSaving(false));
    }

    function onFinishEdit(values: any) {
        vulSourceService.update(vulSourceId!, values)
            .then(
                () => {
                    message.success("Vulnerability source updated.");

                    setEditMode(false);

                    reload();
                },
                error => serverViolationsHolder.handleServerError(error, editForm)
            );
    }

    function onDeleteConfirm(vulSource: VulSource) {
        vulSourceService.delete(vulSource)
            .then(() => {
                message.success("Vulnerability source deleted.");

                navigate(`/vul-sources`)
            });
    }

    function reload() {
        return vulSourceService.get(vulSourceId!).then(vulSource => {
            setSource(vulSource);

            editForm.setFieldsValue({
                ...vulSource,
                organization: vulSource.organization?.id
            });

            setOwner(vulSource.owner);
        });
    }

    function getAssignedAiExtractors(
        aiExtractorAssignments: VulSource["aiExtractorAssignments"],
        scope: ResourceOwnerType
    ): AiExtractor[] {
        return (aiExtractorAssignments ?? [])
            .filter(assignment => assignment.scope === scope)
            .map(assignment => aiExtractorById.get(assignment.id))
            .filter((aiExtractor): aiExtractor is AiExtractor => !!aiExtractor)
            .filter(aiExtractor => !isActivelyManagedByInterests(aiExtractor))
            .sort(compareAssignedAiExtractors);
    }

    function getEditableAssignedAiExtractorIds(aiExtractors: AiExtractor[]): string[] {
        return aiExtractors
            .flatMap(aiExtractor => aiExtractor.id ? [aiExtractor.id] : [])
            .filter(id => editableAiExtractorIds.has(id));
    }

    function shouldShowSystemAiExtractorAssignments() {
        return systemAssignedAiExtractors.length > 0 || canEditSystemAiExtractorAssignments();
    }

    function canEditSystemAiExtractorAssignments() {
        return appContext.user.isSystemAdmin() && source.owner === ResourceOwnerType.SYSTEM;
    }

    function shouldShowOrganizationAiExtractorAssignments() {
        return organizationAssignedAiExtractors.length > 0 || canEditOrganizationAiExtractorAssignments();
    }

    function canEditOrganizationAiExtractorAssignments() {
        if (source.owner === ResourceOwnerType.SYSTEM) {
            return !appContext.user.isSystemAdmin()
                   && appContext.user.hasPermission(Permission.AI_EXTRACTOR__MANAGE_ORG);
        }

        return source.owner === ResourceOwnerType.ORGANIZATION
               && (appContext.user.isSystemAdmin() || appContext.user.hasPermission(Permission.AI_EXTRACTOR__MANAGE_ORG));
    }

    function renderAiExtractorAssignmentEditor(
        selectedIds: string[],
        setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>,
        options: ReturnType<typeof buildAiExtractorSelectOptions>
    ) {
        return (
            <SelectAllMultiSelect
                showSearch
                virtual={false}
                optionFilterProp="label"
                style={{width: '100%'}}
                value={selectedIds}
                onChange={setSelectedIds}
                selectAllValues={getSelectOptionValues(options)}
                options={options}
            />
        );
    }

    function buildOrganizationAssignmentAiExtractorOptions() {
        if (source.owner === ResourceOwnerType.SYSTEM) {
            return buildAiExtractorSelectOptions(
                editableAiExtractors,
                appContext.user.isSystemAdmin(),
                ResourceOwnerType.ORGANIZATION,
                appContext.user.organization?.id
            );
        }

        return buildAiExtractorSelectOptions(
            editableAiExtractors,
            appContext.user.isSystemAdmin(),
            undefined,
            source.organization?.id
        );
    }

    function getSelectOptionValues(options: ReturnType<typeof buildAiExtractorSelectOptions>) {
        return options
            .flatMap(option => "options" in option ? option.options.map(child => child.value) : option.value)
            .filter((value): value is string => !!value);
    }

    function compareAssignedAiExtractors(left: AiExtractor, right: AiExtractor) {
        const leftOwnerOrder = left.owner === ResourceOwnerType.SYSTEM ? 0 : 1;
        const rightOwnerOrder = right.owner === ResourceOwnerType.SYSTEM ? 0 : 1;

        if (leftOwnerOrder !== rightOwnerOrder) {
            return leftOwnerOrder - rightOwnerOrder;
        }

        return (left.name ?? "").localeCompare(right.name ?? "");
    }

    function reloadViews() {
        return vulViewService.getListOfUsedForSource(tableHandler.queryOptions, vulSourceId).then(vulViews => {
            tableHandler.updateTotal(vulViews.total);

            setViews(vulViews);
        })
    }

}

export default VulSourceDetail;
